import { useEffect, useState } from "react";
import PageBreadcrumbs from "../layouts/PageBreadcrumbs.js";
import { SkeletonTable } from "../components/Skeleton.js";
import { api, ApiError, SecretStatus, loadSession } from "../lib/api.js";
import { useTableState } from "../lib/useTableState.js";
import TableToolbar from "../components/TableToolbar.js";

type WorkspaceSecurity = Awaited<ReturnType<typeof api.workspaceSecurity>>;

function statusPill(s: SecretStatus["status"]) {
  if (s === "configured") return <span className="pill pill-ok">Configured</span>;
  if (s === "rotating")   return <span className="pill pill-info">Rotating</span>;
  return <span className="pill pill-err">Missing</span>;
}

function kindLabel(k: SecretStatus["kind"]): string {
  switch (k) {
    case "dhis2-pat":        return "DHIS2 PAT";
    case "webhook-signing":  return "Webhook signing";
    case "forwarding-token": return "Forwarding token";
    case "portal-access-key": return "Portal access key";
    default:                 return k;
  }
}

export default function Security() {
  const [rows, setRows] = useState<SecretStatus[]>([]);
  const [posture, setPosture] = useState<WorkspaceSecurity | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const tEvents = useTableState((posture?.recentSecurityEvents ?? []) as unknown as Array<Record<string, unknown>>, {
    searchFields: ["action", "actorUserId", "actorRole", "severity"],
    initialPageSize: 25
  });
  const tSecrets = useTableState(rows as unknown as Array<Record<string, unknown>>, {
    searchFields: ["id", "label", "kind", "storage", "status", "exposure"],
    initialPageSize: 25
  });

  useEffect(() => {
    let cancelled = false;
    const session = loadSession();
    const wsId = session?.workspaceId;
    Promise.all([
      api.security(),
      wsId ? api.workspaceSecurity(wsId).catch(() => null) : Promise.resolve(null)
    ])
      .then(([sec, ws]) => {
        if (cancelled) return;
        setRows(sec.secrets ?? []);
        setPosture(ws);
      })
      .catch((e: unknown) => { if (!cancelled) setError(e instanceof ApiError ? e.message : String(e)); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  return (
    <div className="page">
      <PageBreadcrumbs />
      <header className="page-head">
        <div>
          <h1>Security</h1>
          <p>All encrypted secrets known to the platform. Values are never exposed; only status is shown.</p>
        </div>
      </header>

      {error && <div className="alert alert-error">{error}</div>}

      {posture && (
        <section className="section">
          <div className="section-head"><h2>Workspace posture</h2></div>
          <div className="stat-grid">
            <div className="stat">
              <div className="stat-label">Members</div>
              <div className="stat-value">{posture.memberCount}</div>
            </div>
            <div className="stat">
              <div className="stat-label">MFA enabled</div>
              <div className="stat-value">{posture.mfaEnabledCount}</div>
              <div className="stat-hint">{posture.memberCount > 0 ? Math.round((posture.mfaEnabledCount / posture.memberCount) * 100) : 0}%</div>
            </div>
            <div className="stat">
              <div className="stat-label">Connections needing rotation</div>
              <div className="stat-value" style={{ color: posture.connectionsNeedingRotation > 0 ? "#b91c1c" : undefined }}>
                {posture.connectionsNeedingRotation}
              </div>
            </div>
          </div>
          {posture.recentSecurityEvents.length > 0 && (
            <div className="table-scroll" style={{ marginTop: 12 }}>
              <TableToolbar
                search={tEvents.search} setSearch={tEvents.setSearch}
                page={tEvents.page} setPage={tEvents.setPage}
                pageSize={tEvents.pageSize} setPageSize={tEvents.setPageSize}
                totalPages={tEvents.totalPages} totalRows={tEvents.totalRows}
                hideExport
                searchPlaceholder="Search events…"
              />
              <table className="data-table">
                <thead><tr><th>When</th><th>Action</th><th>Actor</th><th>Severity</th></tr></thead>
                <tbody>
                  {(tEvents.paged as unknown as typeof posture.recentSecurityEvents).map((e) => (
                    <tr key={e.id}>
                      <td className="soft">{new Date(e.occurredAt).toLocaleString()}</td>
                      <td>{e.action}</td>
                      <td className="soft">{e.actorUserId ?? e.actorRole}</td>
                      <td><span className={`pill ${e.severity === "critical" ? "pill-err" : e.severity === "warning" ? "pill-warning" : "pill-muted"}`}>{e.severity ?? "info"}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      )}

      {loading ? <SkeletonTable rows={5} columns={4} /> :
       rows.length === 0 ? (
        <div className="empty">
          <p><strong>No secrets tracked yet.</strong></p>
          <p className="muted">
            Secrets (DHIS2 PATs, basic-auth credentials, webhook signing keys) are vaulted here.
            Add a <a href="/app/instances">DHIS2 instance</a> with credentials to populate this page.
          </p>
        </div>
       ) : (
        <div className="table-scroll">
          <TableToolbar
            search={tSecrets.search} setSearch={tSecrets.setSearch}
            page={tSecrets.page} setPage={tSecrets.setPage}
            pageSize={tSecrets.pageSize} setPageSize={tSecrets.setPageSize}
            totalPages={tSecrets.totalPages} totalRows={tSecrets.totalRows}
            hideExport
            searchPlaceholder="Search secrets…"
          />
          <table className="data-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Label</th>
                <th>Kind</th>
                <th>Storage</th>
                <th>Status</th>
                <th>Exposure</th>
                <th>Last verified</th>
              </tr>
            </thead>
            <tbody>
              {(tSecrets.paged as unknown as SecretStatus[]).map((s) => (
                <tr key={s.id}>
                  <td><code>{s.id}</code></td>
                  <td>{s.label}</td>
                  <td>{kindLabel(s.kind)}</td>
                  <td className="soft">{s.storage}</td>
                  <td>{statusPill(s.status)}</td>
                  <td><span className="pill pill-muted">{s.exposure}</span></td>
                  <td className="soft">{s.lastValidatedAt ? new Date(s.lastValidatedAt).toLocaleString() : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
       )}
    </div>
  );
}
